import { Box, Typography, Button, Stack, Chip, alpha } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import NotificationsIcon from "@mui/icons-material/Notifications";
import HeroDashboard from "../../images/HeroDashboard.webp";
import useSiteImages from "../../hooks/useSiteImages";

const ROSE = "#9e4562";
const ROSE_LIGHT = "#c4466e";
const ROSE_DARK = "#6b2c47";
const PEACH = "#a94b2c";
const INK = "#1a0d12";

const FloatingCard = ({ children, sx }) => (
  <Box sx={{
    bgcolor: "#ffffff",
    borderRadius: { xs: "6px", md: "8px" },
    px: { xs: 0.9, md: 1.8 },
    py: { xs: 0.6, md: 1.2 },
    boxShadow: "0 8px 32px rgba(107,44,71,0.12), 0 2px 8px rgba(0,0,0,0.06)",
    border: `1px solid rgba(184,92,122,0.12)`,
    backdropFilter: "blur(12px)",
    maxWidth: { xs: 128, sm: 170, md: "none" },
    ...sx,
  }}>
    {children}
  </Box>
);

export default function Hero({ onGetStarted, onViewDemo }) {
  const siteImages = useSiteImages();
  const heroImgSrc = siteImages?.homepage_hero?.url || HeroDashboard;
  return (
    <Box sx={{
      position: "relative",
      background: `linear-gradient(160deg, #fdf5f8 0%, #f9eff4 50%, #f5e8f0 100%)`,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}>
      {/* Dot texture */}
      <Box sx={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: `radial-gradient(rgba(184,92,122,0.04) 1px, transparent 1px)`,
        backgroundSize: "28px 28px",
      }} />
      {/* Rose glow top left */}
      <Box sx={{
        position: "absolute", top: -200, left: -150,
        width: 700, height: 700, borderRadius: "50%",
        background: `radial-gradient(circle, rgba(184,92,122,0.07) 0%, transparent 60%)`,
        pointerEvents: "none",
      }} />
      {/* Peach glow bottom right */}
      <Box sx={{
        position: "absolute", bottom: -200, right: -100,
        width: 600, height: 600, borderRadius: "50%",
        background: `radial-gradient(circle, rgba(212,120,90,0.06) 0%, transparent 65%)`,
        pointerEvents: "none",
      }} />

      <Box sx={{
        position: "relative",
        maxWidth: 1200, mx: "auto", width: "100%",
        px: { xs: 2.5, md: 6 },
        pt: { xs: 6, md: 8 },
        pb: { xs: 8, md: 11 },
        display: { xs: "flex", md: "flex", lg: "grid" },
        flexDirection: { xs: "column", md: "row" },
        gridTemplateColumns: { lg: "minmax(0, 1fr) minmax(0, 650px)" },
        alignItems: "flex-start",
        gap: { xs: 7, md: 8 },
      }}>
        {/* LEFT copy */}
        <Box sx={{
          flex: "0 0 auto", maxWidth: 530,
          "@keyframes heroSlideLeft": { from: { transform: "translateX(-28px)" }, to: { transform: "translateX(0)" } },
          animation: "heroSlideLeft 0.7s cubic-bezier(0.22,1,0.36,1) both",
        }}>

          {/* Keyword-targeted H1 for SEO — visible to crawlers and screen readers */}
          <Typography component="h1" sx={{
            position: "absolute",
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: "hidden",
            clip: "rect(0,0,0,0)",
            whiteSpace: "nowrap",
            border: 0,
          }}>
            All-in-One Salon Management Software for India
          </Typography>

          {/* Visual headline as H2 */}
          <Typography component="h2" sx={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontWeight: 600,
            fontSize: { xs: "2.4rem", sm: "3.2rem", md: "4rem" },
            lineHeight: 1.12,
            letterSpacing: "-0.01em",
            color: INK,
            mb: 1.5,
          }}>
            <Box component="span" sx={{ display: "block", whiteSpace: "nowrap" }}>
              The smarter
            </Box>
            <Box component="span" sx={{ display: "block", whiteSpace: "nowrap" }}>
              way to run
            </Box>
            <Box component="span" sx={{ display: "block", whiteSpace: "nowrap" }}>
              <Box component="em" sx={{
                fontStyle: "italic",
                background: `linear-gradient(135deg, ${ROSE_LIGHT} 0%, ${ROSE} 60%, ${ROSE_DARK} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>
                your salon.
              </Box>
            </Box>
          </Typography>

          <Typography sx={{
            color: "#6a4e62",
            fontSize: { xs: "1rem", md: "1.05rem" },
            lineHeight: 1.85,
            mb: 4,
            maxWidth: 480,
            fontWeight: 400,
          }}>
            Appointments, staff, inventory, memberships & automated marketing —
            all in one platform built for Indian salons & spas.
          </Typography>

          {/* Feature chips */}
          <Stack direction="row" flexWrap="wrap" gap={1} mb={4.5}>
            {[
              { icon: <EventAvailableIcon sx={{ fontSize: "0.8rem !important" }} />, label: "Smart Booking" },
              { icon: <PeopleAltIcon sx={{ fontSize: "0.8rem !important" }} />, label: "Staff Scheduling" },
              { icon: <TrendingUpIcon sx={{ fontSize: "0.8rem !important" }} />, label: "Revenue Analytics" },
              { icon: <CheckCircleOutlineIcon sx={{ fontSize: "0.8rem !important" }} />, label: "Auto Reminders" },
            ].map(({ icon, label }) => (
              <Chip
                key={label}
                icon={icon}
                label={label}
                size="small"
                sx={{
                  bgcolor: alpha(ROSE, 0.07),
                  color: ROSE_DARK,
                  fontWeight: 500,
                  fontSize: "0.73rem",
                  border: `1.5px solid rgba(184,92,122,0.18)`,
                  borderRadius: "20px",
                  "& .MuiChip-icon": { color: ROSE },
                }}
              />
            ))}
          </Stack>

          {/* CTAs */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={4}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon sx={{ fontSize: "0.9rem !important" }} />}
              onClick={onGetStarted}
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
                px: 3.5, py: 1.6,
                fontSize: { xs: "0.8rem", sm: "0.88rem" },
                boxShadow: `0 8px 28px rgba(107,44,71,0.22)`,
                "&:hover": { boxShadow: `0 14px 36px rgba(107,44,71,0.3)` },
                transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              Book a Free Demo
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<PlayCircleOutlineIcon />}
              onClick={onViewDemo}
              sx={{
                borderWidth: "1.5px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                px: 3, py: 1.6,
                fontSize: { xs: "0.8rem", sm: "0.88rem" },
                color: "#6a4e62",
                borderColor: "rgba(106,78,98,0.3)",
                "&:hover": {
                  borderColor: alpha(ROSE, 0.5),
                  color: ROSE,
                  background: alpha(ROSE, 0.04),
                  borderWidth: "1.5px",
                },
              }}
            >
              Watch Demo
            </Button>
          </Stack>

          {/* Trust */}
          <Stack direction="row" flexWrap="wrap" gap={2.5}>
            {["Free onboarding & training", "Personalized live demo", "Setup in 30 minutes"].map((txt) => (
              <Box key={txt} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: "0.9rem", color: alpha(ROSE, 0.65) }} />
                <Typography sx={{ color: "#89647d", fontWeight: 400, fontSize: "0.78rem" }}>
                  {txt}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* RIGHT mockup */}
        <Box sx={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2,
          "@keyframes heroSlideRight": {
            from: { opacity: 0, transform: "translateX(40px) scale(0.97)" },
            to: { opacity: 1, transform: "translateX(0) scale(1)" },
          },
          animation: "heroSlideRight 0.75s cubic-bezier(0.22,1,0.36,1) 0.2s both",
        }}>
          {/* Badge */}
          <Box sx={{
            "@keyframes heroSlideDown": { from: { transform: "translateY(-8px)" }, to: { transform: "translateY(0)" } },
            animation: "heroSlideDown 0.5s ease 0.15s both",
          }}>
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              border: `1.5px solid rgba(184,92,122,0.2)`,
              borderRadius: "20px",
              px: 2, py: 0.8,
              background: alpha(ROSE, 0.06),
            }}>
              {/* Composited live-pulse dot — ring uses transform+opacity only (no box-shadow paint) */}
              <Box sx={{ position: "relative", width: 6, height: 6, flexShrink: 0 }}>
                {/* Expanding ring: GPU-composited transform + opacity */}
                <Box
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    top: "50%", left: "50%",
                    width: 6, height: 6,
                    borderRadius: "50%",
                    bgcolor: ROSE_LIGHT,
                    "@keyframes livePulse": {
                      "0%":   { transform: "translate(-50%,-50%) scale(1)",   opacity: 0.6 },
                      "70%":  { transform: "translate(-50%,-50%) scale(3.2)", opacity: 0 },
                      "100%": { transform: "translate(-50%,-50%) scale(3.2)", opacity: 0 },
                    },
                    animation: "livePulse 2s ease-out infinite",
                    willChange: "transform, opacity",
                    pointerEvents: "none",
                  }}
                />
                {/* Static solid dot */}
                <Box sx={{
                  position: "absolute",
                  top: "50%", left: "50%",
                  transform: "translate(-50%,-50%)",
                  width: 6, height: 6,
                  borderRadius: "50%",
                  bgcolor: ROSE_LIGHT,
                }} />
              </Box>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: ROSE, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Trusted by 500+ salons across India
              </Typography>
            </Box>
          </Box>

          <Box sx={{ position: "relative", width: "100%", maxWidth: { xs: "100%", md: 545, lg: 650 } }}>
            {/* Glow */}
            <Box sx={{
              position: "absolute", top: "10%", left: "5%",
              width: "90%", height: "80%",
              background: `radial-gradient(ellipse, rgba(184,92,122,0.08) 0%, transparent 70%)`,
              filter: "blur(32px)",
              zIndex: 0, pointerEvents: "none",
            }} />

            {/* Dashboard */}
            <Box
              component="img"
              src={heroImgSrc}
              alt="Ayesalon salon management dashboard showing today's appointments, revenue, and staff schedule"
              sx={{
                position: "relative", zIndex: 1,
                width: "100%",
                aspectRatio: "1548 / 946",
                objectFit: "cover",
                display: "block",
                borderRadius: "12px",
              }}
            />

            {false && (<>
            {/* Floating — New Booking */}
            <Box sx={{
              position: "absolute", top: { xs: "-3%", md: "6%" }, left: { xs: "-3%", md: "-8%" }, zIndex: 3,
              "@keyframes heroFloatUp": { from: { opacity: 0, transform: "translateY(14px)" }, to: { opacity: 1, transform: "translateY(0)" } },
              animation: "heroFloatUp 0.55s cubic-bezier(0.22,1,0.36,1) 0.9s both",
            }}>
              <FloatingCard>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.7, md: 1.2 } }}>
                  <Box sx={{
                    width: { xs: 24, md: 36 }, height: { xs: 24, md: 36 }, borderRadius: { xs: "6px", md: "8px" }, flexShrink: 0,
                    bgcolor: alpha(ROSE, 0.1),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: `1px solid rgba(184,92,122,0.2)`,
                  }}>
                    <CalendarMonthIcon sx={{ fontSize: { xs: "0.8rem", md: "1.1rem" }, color: ROSE }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: { xs: "0.56rem", md: "0.67rem" }, fontWeight: 700, color: INK, lineHeight: 1.3, whiteSpace: "nowrap" }}>New Booking</Typography>
                    <Typography sx={{ fontSize: { xs: "0.48rem", md: "0.6rem" }, color: "#89647d", lineHeight: 1.3, display: { xs: "none", sm: "block" } }}>Priya · Hair Color · 3:00 PM</Typography>
                  </Box>
                </Box>
              </FloatingCard>
            </Box>

            {/* Floating — Revenue */}
            <Box sx={{
              position: "absolute", bottom: { xs: "20%", md: "14%" }, right: { xs: "-3%", md: "-6%" }, zIndex: 3,
              animation: "heroFloatUp 0.55s cubic-bezier(0.22,1,0.36,1) 1.1s both",
            }}>
              <FloatingCard>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.7, md: 1.2 } }}>
                  <Box sx={{
                    width: { xs: 24, md: 36 }, height: { xs: 24, md: 36 }, borderRadius: { xs: "6px", md: "8px" }, flexShrink: 0,
                    bgcolor: alpha(ROSE_LIGHT, 0.1),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: `1px solid rgba(196,70,110,0.2)`,
                  }}>
                    <TrendingUpIcon sx={{ fontSize: { xs: "0.8rem", md: "1.1rem" }, color: ROSE_LIGHT }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: { xs: "0.56rem", md: "0.67rem" }, fontWeight: 700, color: ROSE_LIGHT, lineHeight: 1.3, whiteSpace: "nowrap" }}>Revenue +32%</Typography>
                    <Typography sx={{ fontSize: { xs: "0.48rem", md: "0.6rem" }, color: "#89647d", lineHeight: 1.3, display: { xs: "none", sm: "block" } }}>vs. last month</Typography>
                  </Box>
                </Box>
              </FloatingCard>
            </Box>

            {/* Floating — Reminder */}
            <Box sx={{
              position: "absolute", bottom: { xs: "-2%", md: "36%" }, left: { xs: "20%", md: "-7%" }, zIndex: 3,
              "@keyframes heroFloatDown": { from: { opacity: 0, transform: "translateY(-10px)" }, to: { opacity: 1, transform: "translateY(0)" } },
              animation: "heroFloatDown 0.5s cubic-bezier(0.22,1,0.36,1) 1.3s both",
            }}>
              <FloatingCard>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.7, md: 1.2 } }}>
                  <Box sx={{
                    width: { xs: 24, md: 36 }, height: { xs: 24, md: 36 }, borderRadius: { xs: "6px", md: "8px" }, flexShrink: 0,
                    bgcolor: alpha(PEACH, 0.1),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: `1px solid rgba(212,120,90,0.2)`,
                  }}>
                    <NotificationsIcon sx={{ fontSize: { xs: "0.8rem", md: "1.1rem" }, color: PEACH }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: { xs: "0.56rem", md: "0.67rem" }, fontWeight: 700, color: PEACH, lineHeight: 1.3, whiteSpace: "nowrap" }}>Reminder Sent</Typography>
                    <Typography sx={{ fontSize: { xs: "0.48rem", md: "0.6rem" }, color: "#89647d", lineHeight: 1.3, display: { xs: "none", sm: "block" } }}>No-shows ↓ 40% this week</Typography>
                  </Box>
                </Box>
              </FloatingCard>
            </Box>
            </>)}
          </Box>
        </Box>
      </Box>

      {/* Bottom accent */}
      <Box sx={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: "1px",
        pointerEvents: "none",
        background: `linear-gradient(90deg, transparent 0%, rgba(184,92,122,0.3) 35%, rgba(184,92,122,0.5) 65%, transparent 100%)`,
      }} />
    </Box>
  );
}
